import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import Textlink from '../../elements/Textlink';
import theme from '../../constants/Theme';
import { Headline } from '../../elements';
import Dialog from '../Dialog';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

interface Situation {
  id: string;
  count: number;
  description: string;
}

interface MostUsedSituationsWidgetProps {
  situations: Situation[];
  isLoading?: boolean;
}

const MostUsedSituationsWidget: React.FC<MostUsedSituationsWidgetProps> = ({ situations, isLoading = false }) => {
  const classes = useStyles();
  const visibleSituations = situations.slice(0, 5);
  const hasMoreSituations = situations.length > 5;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const renderRow = (situation: Situation) => (
    <div key={situation.id} className={classes.row}>
      <span title={situation.description} data-toggle="tooltip" className={classes.situation}>
        {situation.description}
      </span>
      <span className={classes.count}>{situation.count} mal</span>
    </div>
  );

  return (
    <div className={classes.container}>
      <LoadingOverlay isLoading={isLoading} />
      <Headline fontSize={20} marginBottom={30}>
        Häufigste Situationen
      </Headline>
      <div className={classes.content}>
        <div>{visibleSituations.map(renderRow)}</div>
        {hasMoreSituations && (
          <>
            <div className={classes.linkContainer}>
              <Textlink title="Alle anzeigen" onClick={() => setIsDialogOpen(true)} fontSize={14} underline />
            </div>
            <Dialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} title="Häufigste Situationen">
              <div>{situations.map(renderRow)}</div>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    backgroundColor: theme.colors.contrastWhite,
    padding: 15,
    borderRadius: theme.borderRadius.card,
    boxShadow: theme.shadows.card,
    position: 'relative',
    height: '349px',
    boxSizing: 'border-box',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 10,
    alignItems: 'center',
    fontFamily: theme.typography.fontFamily,
    fontSize: 18,
    lineHeight: '22px',
    marginBottom: '22px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  situation: {
    color: theme.colors.blue,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  count: {
    color: theme.colors.blue,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  linkContainer: {
    marginTop: 4,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

export default MostUsedSituationsWidget;
